import i18n from '@/plugins/I18n';
import { I18N_KEY } from '@/constants/I18n';
import { validateLogin } from '@/helpers/validators';
import { ValidationProvider, localize, extend, ValidationObserver } from 'vee-validate';

import {
  confirmed,
  email,
  min,
  regex,
  required,
  integer,
  max,
  image,
  numeric,
  between,
  min_value,
  max_value,
  mimes,
} from 'vee-validate/dist/rules';

import ru from 'vee-validate/dist/locale/ru.json';

import { clearHtmlTags } from '../utils/parser';
import { isDate } from 'date-fns';
import { isNil, isObject } from 'lodash';

const PREFIX_TYPES = {
  base: 'base',
  some: 'some',
};

const PREFIX_TYPES_DATA = {
  [PREFIX_TYPES.base]: {
    id: PREFIX_TYPES.base,
    name: '',
    validate: (a, b) => a === b,
  },
  [PREFIX_TYPES.some]: {
    id: PREFIX_TYPES.some,
    name: 'хотя бы',
    validate: (a, b) => a <= b,
  },
};

const DEFAULT_PLURAL_OPTIONS = {
  caseType: 'basic',
  count: 1,
  withCount: false,
};

const getPlural = (name, options) => {
  const targetName = I18N_KEY[name] || I18N_KEY.value;
  const extraOptions = {
    ...DEFAULT_PLURAL_OPTIONS,
    ...options,
  };
  const { caseType, count, withCount } = extraOptions;
  const key = withCount ? 'count' : 'score';
  const args = [
    `${targetName}.${key}.${caseType}`,
    count,
  ];

  return i18n.tc(...args);
};

const getPluralForm = (name, count, prefixData, actionText) => {
  let plural;
  if (count === 'multiple') {
    plural = getPlural(name, { caseType: 'accusative', count: 2 });
  } else if (Number.isInteger(Number(count))) {
    plural = getPlural(name, { caseType: 'accusative', count, withCount: true });
  } else {
    plural = getPlural(name, { caseType: 'accusative', count: 1 });
  }

  if (!plural) return 'Данное поле обязательно для заполнения';

  return `${[actionText, prefixData.name, plural].filter(Boolean).join(' ')}`;
};

const setupExtends = () => {
  extend('mimes', mimes);
  extend('between', between);
  extend('numeric', numeric);
  extend('integer', integer);
  extend('size', {
    params: ['size'],
    validate: (value, { size }) => {
      const files = Array.isArray(value) ? [...value] : [value];

      return files.every((data) => {
        const file = data.file || data;
        return Math.abs(file.size / 1024) < Number(size);
      });
    },
  });
  extend('email', {
    ...email,
    message: 'Должен быть действительный электронный адрес формата: example@mail.ru',
  });
  extend('required', {
    ...required,
    message: 'Данное поле обязательно для заполнения',
  });
  extend('required_fill', {
    ...required,
    message: 'Заполните поле',
  });
  extend('required_select', {
    ...required,
    params: ['count', 'prefixType', 'property', 'expectedValue'],
    validate(value, {
      count = 1,
      property = undefined,
      prefixType = PREFIX_TYPES.base,
      expectedValue = undefined,
    }) {
      const prefixData = PREFIX_TYPES_DATA[prefixType];
      const isRequiredValid = required.validate(value);

      if (!isRequiredValid.valid) return false;

      if (Array.isArray(value)) {
        if (property || expectedValue) {
          return value.some((item) => (
            property
              ? item[property] === expectedValue
              : item === expectedValue
          ));
        }

        return prefixData.validate(Number(count), value.length);
      }

      return true;
    },
    message: (name, { count, prefixType = PREFIX_TYPES.base }) => {
      const prefixData = PREFIX_TYPES_DATA[prefixType];

      return getPluralForm(name, count, prefixData, 'Выберите');
    },
  });
  extend('required_add', {
    ...required,
    params: ['count'],
    validate: (value, { count = 1 }) => {
      const list = value || [];
      return list.length >= count;
    },
    message: (name, { count = 1 }) => {
      const pluralCount = Number.isInteger(count) ? count : 1;
      const pluralName = name || 'value';
      const plural = getPlural(pluralName, {
        caseType: 'accusative',
        count: pluralCount,
        withCount: true,
      });

      return `Добавьте хотя бы ${plural}`;
    },
  });
  extend('confirmed', {
    ...confirmed,
    message: 'Поля не совпадают',
  });
  extend('regex', regex);
  extend('image', image);
  extend('username', {
    validate(val) {
      return validateLogin(val);
    },
    message: 'Вы указали недопустимые символы. Допустимы латинские буквы, цифры и знаки "-", ИЛИ "username#id"',
  });
  extend('username_lowercase', {
    validate(val) {
      return /^[a-z0-9-_]+$/.test(val);
    },
    message: 'Вы указали недопустимые символы. Допустимы латинские строчные буквы, цифры и знак "-"',
  });
  extend('url', {
    validate(value) {
      if (value) {
        // eslint-disable-next-line max-len
        return /^(http:\/\/www\.|https:\/\/www\.|http:\/\/|https:\/\/)?[a-z0-9]+([-.]{1}[a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?(\/.*)?$/.test(
          value,
        );
      }
      return false;
    },
    message: 'Некорректный URL',
  });
  extend('requiredSomeListItem', {
    computesRequired: true,
    params: ['key'],
    validate: (value, { key }) => {
      const list = value || [];
      return list.some((item) => item[key]);
    },
    message: 'Выберите хотя бы одно поле',
  });
  extend('requiredSomeParam', {
    computesRequired: true,
    validate: (value) => {
      const obj = value || {};
      return Object.values(obj).some((item) => !!item);
    },
    message: 'Выберите хотя бы одно значение',
  });
  extend('phone', {
    validate: (value) => /^(\+7|7|8){1}?[\s-]?\(?[489][0-9]{2}\)?[\s-]?[0-9]{3}[\s-]?[0-9]{2}[\s-]?[0-9]{2}$/.test(value),
    message: 'Номер телефона введён не верно',
  });
  extend('included', {
    params: ['target'],
    validate: (value, { target }) => {
      const arr = target ? [].concat(target) : [];
      return !arr.includes(value);
    },
    message: 'Такое значение уже существует',
  });
  extend('password', {
    validate: (value) => /^[^а-яА-ЯёЁ]+$/.test(value),
    message: 'Пароль может содержать только латинские буквы и цифры',
  });
  extend('hard_password', {
    validate: (value) => /^[^а-яА-ЯёЁ]+$/.test(value)
      && /[0-9]/.test(value)
      && /[a-z]/.test(value)
      && /[A-Z]/.test(value),
    message: 'Пароль должен содержать хотя бы одну цифру, одну строчную и одну заглавную латинскую букву. Кириллица недопустима',
  });
  extend('noSpaces', {
    validate: (value) => !/\s/g.test(value),
    message: 'Поле не должно содержать пробелы',
  });
  extend('no_extra_symbols', {
    validate: (value) => /^[a-zA-Zа-яА-ЯёЁ]+$/m.test(value),
    message: 'Поле не должно содержать дополнительных знаков',
  });
  extend('onlyCyrillic', {
    validate: (value) => /^[а-яА-ЯёЁ\- ]+$/.test(value),
    message: 'Допустимо использование исключительно кириллицы без цифр и дополнительных знаков',
  });
  extend('only_integer_numbers', {
    validate: (value) => /^[0-9]+$/m.test(value),
    message: 'Допустимо использование исключительно целочисленных цифр',
  });
  extend('min_value', min_value);
  extend('max_value', max_value);
  extend('max_files', {
    params: ['count'],
    validate: (value, { count }) => !value || (Array.isArray(value) && value.length <= Number(count)),
    message: (name, { count }) => i18n.tc('event_data.documents.fileLimit', Number(count), { count }),
  });
  extend('min_date', {
    validate: (value, date) => {
      const [dateValue] = date;
      if (!dateValue || dateValue === 'null' || dateValue === 'undefined' || dateValue === '0') return true;
      const minDate = new Date(dateValue);
      const currentDate = new Date(value);
      return +currentDate >= +minDate;
    },
    message: 'Дата не может быть раньше начальной',
  });
  extend('max_date', {
    validate: (value, date) => {
      const [dateValue] = date;
      if (!dateValue || dateValue === 'null' || dateValue === 'undefined' || dateValue === '0') return true;
      const maxDate = new Date(dateValue);
      const currentDate = new Date(value);
      return +currentDate <= +maxDate;
    },
    message: 'Дата не может быть позже окончательной',
  });
  extend('period', {
    validate: (value) => {
      const [start, end] = [value || []].flat();
      const minDate = new Date(start || undefined);
      const maxDate = new Date(end) || undefined;
      if (!isDate(minDate) || !isDate(maxDate)) return true;
      return +minDate < +maxDate;
    },
    message: 'Окончательная дата должна быть позже начальной',
  });
  extend('max', {
    ...max,
    validate: (value, { length }) => {
      if (typeof value === 'string') {
        const cleanedValue = clearHtmlTags(value);
        return max.validate(cleanedValue, { length }) || cleanedValue.length < length;
      }

      return max.validate(value, { length });
    },
  });
  extend('min', {
    ...min,
    validate: (value, { length }) => {
      if (typeof value === 'string') {
        const cleanedValue = clearHtmlTags(value);
        return min.validate(cleanedValue, { length }) || cleanedValue.length > length;
      }

      return min.validate(value, { length });
    },
  });
  extend('length', {
    validate: (value, [length]) => {
      if (!value) return true;
      if (Array.isArray(value)) {
        return value.length === Number(length) ? true : `Необходимо ${length} элементов`;
      }
      if (isObject(value)) {
        return Object.values(value).filter((v) => !isNil(v)).length === length ? true : 'Заполните все поля';
      }
      return String(value || '').length === Number(length) ? true : `Необходимо ${length} символов`;
    },
  });
  extend('website', {
    validate: (value, sites) => !!sites.find((site) => (value || '').includes(site) && /^(ftp|http|https):\/\/[^ "]+$/.test(value || '')),
    message: 'Недопустимая ссылка',
  });
  extend('snils', {
    validate: (value) => {
      const gps = String(value).replace(/[^0-9]+/g, '');
      const checkValue = () => {
        if (gps.length === 11) {
          let checksum = 0;
          for (let i = 0; i < 9; i++) {
            checksum += parseInt(gps.charAt(i)) * (9 - i);
          }
          if (checksum > 101) {
            checksum %= 101;
          }
          if (checksum === 100 || checksum === 101) {
            checksum = 0;
          }
          const controlNumber = gps.slice(9);
          return checksum === parseInt(controlNumber);
        }
        return false;
      };
      const isValid = checkValue();
      return !value || isValid;
    },
    message: 'Некорректный СНИЛС',
  });
  extend('min_length', {
    validate: (value, data) => {
      const [length] = data || [];
      if (value?.length) {
        return value.length >= (length || 0);
      }
      return false;
    },
    message: 'Недостаточно элементов в списке',
  });
  extend('min_required_length', {
    validate: (value, data) => {
      const [length] = data || [];
      if (value?.length) {
        return value.filter(Boolean).length >= Number(length || 0);
      }
      return false;
    },
    message: 'Заполните все поля',
  });
  extend('max_length', {
    validate: (value, data) => {
      const [length] = data || [];
      if (value?.length) {
        return value.length <= (length || 0);
      }
      return false;
    },
    message: 'Слишком много элементов в списке',
  });
  extend('boolean', {
    validate: (value) => {
      if (!value) {
        return false;
      }

      return true;
    },
    message: 'Данное поле обязательно для заполнения',
  });
  extend('has_value', {
    validate: (value) => {
      if ([null, undefined].includes(value)) {
        return false;
      }

      return true;
    },
    message: 'Данное поле обязательно для заполнения',
  });
  extend('date_before', {
    params: ['target'],
    validate(value, { target }) {
      if (!value || !target) {
        return true;
      }

      const startDate = new Date(value);
      const endDate = new Date(target);

      return startDate <= endDate;
    },
    message: 'Дата начала должна быть раньше даты окончания',
  });
  extend('date_after', {
    params: ['target'],
    validate(value, { target }) {
      if (!value || !target) {
        return true;
      }

      const startDate = new Date(target);
      const endDate = new Date(value);

      return startDate <= endDate;
    },
    message: 'Дата окончания должна быть после даты начала',
  });
  extend('fio', {
    validate: (value) => String(value).split(' ').length >= 2,
    message: 'Введите в формате "Фамилия Имя Отчество(при наличии)"',
  });
};

export default {
  install(Vue) {
    localize('ru', ru);
    setupExtends();
    Vue.component('validation-provider', ValidationProvider);
    Vue.component('validation-observer', ValidationObserver);
  },
};
